import { useState } from "react";
import { toast } from "sonner";
import { Card, ErrorBanner, Spinner, StatusBadge, fmt, useLoad } from "./kit";
import { LevelBadge, label } from "./Ai";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { api, errMsg } from "../lib/api";

const URGENCY = { low: 0, medium: 1, high: 2, critical: 3 };

const URGENCY_LEVEL = { critical: "critical", high: "high", medium: "medium" };

export function RiskBadge({ risk, testId }) {
  if (!risk) return <span className="text-xs text-zinc-600">—</span>;
  if (!risk.ai_classified) return <span className="text-xs text-zinc-600" title={risk.error}>not scored</span>;
  return <LevelBadge level={risk.level} text={`${risk.level} risk`} testId={testId} />;
}

function FlaggedCard({ m, review, suspend }) {
  const sc = m.screening;
  return (
    <Card testId={`screening-${m.id}`}>
      <div className="flex flex-wrap items-center gap-2"><LevelBadge level="high" text={label(sc.category)} />
        <span className="text-xs text-zinc-500">risk {Math.round(sc.risk * 100)}%</span>{m.test_mode && <span className="text-xs text-fuchsia-300">test mode</span>}<StatusBadge status={m.status} />
        {sc.review_status && <span className="text-xs text-zinc-400">reviewed: {sc.review_status} by {sc.reviewed_by}</span>}</div>
      <div className="mt-2 break-words text-sm font-medium text-zinc-100">{m.subject}</div>
      <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
        <dt className="text-zinc-500">From</dt><dd className="break-all text-zinc-300">{m.from}</dd>
        <dt className="text-zinc-500">To</dt><dd className="break-all text-zinc-300">{(m.to || []).join(", ")}{m.recipients > (m.to || []).length ? ` +${m.recipients - m.to.length}` : ""}</dd>
        <dt className="text-zinc-500">Workspace</dt><dd className="text-zinc-300">{m.workspace_name || m.workspace_id}{m.workspace_suspended && <span className="ml-2 text-red-300">suspended</span>}</dd>
        <dt className="text-zinc-500">Sent</dt><dd className="text-zinc-300">{fmt(m.created_at)} · via {m.source}</dd>
        {sc.link_domains?.length > 0 && <><dt className="text-zinc-500">Links to</dt><dd className="break-all text-amber-200">{sc.link_domains.join(", ")}</dd></>}
        {sc.attachment_names?.length > 0 && <><dt className="text-zinc-500">Files</dt><dd className="break-all text-zinc-300">{sc.attachment_names.join(", ")}</dd></>}
      </dl>
      {sc.excerpt && <pre className="mt-3 max-h-48 overflow-auto whitespace-pre-wrap rounded-lg border border-white/10 bg-black/40 p-3 text-xs text-zinc-300" data-testid={`screening-excerpt-${m.id}`}>{sc.excerpt}</pre>}
      <div className="mt-3 flex flex-wrap gap-2">
        <Button size="sm" variant="outline" className="border-white/10 bg-transparent hover:bg-white/5 hover:text-white" onClick={() => review(m.id, "ok")} data-testid={`screening-ok-${m.id}`}>Looks fine</Button>
        <Button size="sm" variant="ghost" className="text-red-300 hover:bg-red-500/10 hover:text-red-200" onClick={() => review(m.id, "abuse")} data-testid={`screening-abuse-${m.id}`}>Confirm abuse</Button>
        {!m.workspace_suspended && <Button size="sm" variant="ghost" className="text-red-300 hover:bg-red-500/10" onClick={() => suspend(m)} data-testid={`screening-suspend-${m.id}`}>Suspend workspace</Button>}
      </div>
    </Card>
  );
}

export function Screening() {
  const [all, setAll] = useState(false);
  const { data, error, loading, reload } = useLoad(() => api.get("/admin/screening", { params: { include_reviewed: all } }).then((r) => r.data), [all]);
  const review = async (id, review_status) => {
    try { await api.patch(`/admin/screening/${id}`, { review_status }); toast.success(review_status === "ok" ? "Marked as fine" : "Marked as abuse"); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const suspend = async (m) => {
    const reason = window.prompt(`Suspend ${m.workspace_name || m.workspace_id}? Reason shown to the customer:`, `Flagged ${label(m.screening.category)} email`);
    if (!reason) return;
    try { await api.patch(`/admin/workspaces/${m.workspace_id}`, { suspended: true, suspended_reason: reason }); toast.success("Workspace suspended"); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (loading && !data) return <Spinner />;
  if (error) return <ErrorBanner error={error} />;
  return (
    <div className="space-y-3" data-testid="admin-screening">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-2xl text-xs text-zinc-500">Customers' outgoing emails that automatic screening flagged as possible spam, phishing, malware or scams. Your own workspaces are never screened. A short text excerpt is kept for flagged emails only.</p>
        <label className="flex items-center gap-2 text-xs text-zinc-400"><Switch checked={all} onCheckedChange={setAll} data-testid="screening-show-reviewed" />Show reviewed</label>
      </div>
      {!data.length ? <p className="text-sm text-zinc-500" data-testid="admin-screening-empty">Nothing waiting for review.</p>
        : data.map((m) => <FlaggedCard key={m.id} m={m} review={review} suspend={suspend} />)}
    </div>
  );
}

export function AbuseReports() {
  const { data, error, loading, reload } = useLoad(() => api.get("/admin/abuse-reports").then((r) => r.data));
  if (loading) return <Spinner />;
  if (error) return <ErrorBanner error={error} />;
  if (!data.length) return <p className="text-sm text-zinc-500" data-testid="admin-abuse-empty">No abuse reports.</p>;
  const sorted = [...data].sort((a, b) => (a.status === "resolved") - (b.status === "resolved") || (URGENCY[b.triage?.urgency] ?? -1) - (URGENCY[a.triage?.urgency] ?? -1));
  return (
    <div className="space-y-2" data-testid="admin-abuse">{sorted.map((r) => (
      <Card key={r.id}><div className="flex justify-between gap-4"><div className="min-w-0">
        {r.triage?.ai_classified && <div className="mb-2 flex gap-2" data-testid={`abuse-triage-${r.id}`}><LevelBadge level={URGENCY_LEVEL[r.triage.urgency] || "low"} text={`${r.triage.urgency} urgency`} />
          <LevelBadge text={label(r.triage.category)} /></div>}
        <div className="text-sm text-zinc-200">{r.details}</div><div className="mt-1 text-xs text-zinc-500">{r.reporter_email} · msg {r.message_id || "—"} · ws {r.workspace_id || "unknown"} · {fmt(r.created_at)}</div></div>
        <select value={r.status} className="h-8 rounded-md border border-white/10 bg-[#111017] px-2 text-xs" aria-label="Report status" data-testid={`abuse-status-${r.id}`}
          onChange={(e) => api.patch(`/admin/abuse-reports/${r.id}`, { status: e.target.value }).then(() => { toast.success("Updated"); reload(); }).catch((er) => toast.error(errMsg(er)))}>
          {["open", "investigating", "resolved"].map((s) => <option key={s}>{s}</option>)}</select></div></Card>))}
    </div>
  );
}
